import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, errText } from "@/lib/api";
import { usePrices, money } from "./usePrices";
import { PayPalCheckout } from "./PayPalCheckout";
import { upcomingMoons, moonLabel } from "@/lib/content";

const OPTIONS = [["psychic-reading", "Psychic Reading"], ["moon-ritual", "Moon Ritual"]];
const TIMES = ["Morning", "Afternoon", "Evening", "Flexible"];
const RITUALS = ["Manifestation", "Protection", "Love", "Prosperity & career", "Other"];
const METHODS = [["video", "Video meeting link"], ["phone", "Phone call"]];
const field = "w-full bg-transparent border-b border-[#D9D1C2] focus:border-[#A8613C] outline-none py-2.5 text-[#2D2A26] placeholder:text-[#8A8377] transition-colors";

export const BookingDialog = ({ open, service, moon, onOpenChange }) => {
  const prices = usePrices();
  const moons = upcomingMoons();
  const [form, setForm] = useState({ service, name: "", email: "", phone: "", contact_method: "video", duration: 30, moon_cycle: "", ritual_type: "Manifestation", preferred_date: "", preferred_time: "Flexible", notes: "" });
  const isRitual = form.service === "moon-ritual";
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(false);
  useEffect(() => { if (open) { setForm((f) => ({ ...f, service, moon_cycle: moon || f.moon_cycle || (moons[0] ? moonLabel(moons[0]) : "") })); setDone(false); } }, [open, service, moon]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please share your name and email."); return; }
    if (!isRitual && form.contact_method === "phone" && !form.phone.trim()) { toast.error("Please add a phone number for your call."); return; }
    if (isRitual && !form.notes.trim()) { toast.error("Please share the desires you want to manifest."); return; }
    if (isRitual && !form.moon_cycle) { toast.error("Please choose a moon cycle."); return; }
    setBusy(true);
    try {
      const payload = isRitual
        ? { ...form, duration: null, contact_method: "", phone: "", preferred_date: "", preferred_time: "" }
        : { ...form, ritual_type: "", moon_cycle: "", phone: form.contact_method === "phone" ? form.phone : "" };
      const { data } = await http.post("/bookings", payload);
      setDone(data?.booking_id || true);
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-modal" className="max-w-xl bg-[#FBFBF8] border-[#E6E0D4] rounded-[28px] p-7 sm:p-10 max-h-[92vh] overflow-y-auto">
        {done ? (
          <div data-testid="booking-success" className="py-4 text-center">
            <span className="mx-auto mb-6 grid place-items-center w-14 h-14 rounded-full bg-[#EBF0EC] text-[#4F5F50]"><Check /></span>
            <DialogTitle className="font-display text-4xl font-light text-[#2D2A26]">Your request is received</DialogTitle>
            <DialogDescription className="mt-3 text-[#6E685F]">{isRitual ? "Diana will reach out by email to confirm your ritual and the moon cycle it will be performed in." : "Diana will reach out by email to confirm your reading time."} Thank you for trusting Shaya.</DialogDescription>
            <PayPalCheckout bookingId={done} amount={isRitual ? prices.ritual : prices.options.find((o) => o.minutes === form.duration)?.price} label={isRitual ? "moon ritual" : `${form.duration}-minute reading`} />
          </div>
        ) : (
          <>
            <DialogTitle className="font-display text-4xl font-light text-[#2D2A26]">Book a session</DialogTitle>
            <DialogDescription className="text-[#6E685F] -mt-2">{isRitual ? "Submit your desires — your ritual is performed during the new and full moon cycles." : "Readings are held by video call or phone — your choice."}</DialogDescription>
            <div data-testid="booking-form-service-select" className="grid grid-cols-2 gap-3">
              {OPTIONS.map(([id, label]) => (
                <button key={id} type="button" data-testid={`booking-service-${id}`} onClick={() => setForm((f) => ({ ...f, service: id }))}
                  className={`rounded-2xl border px-4 py-4 text-left transition-colors ${form.service === id ? "border-[#A8613C] bg-[#F4F0E8]" : "border-[#E6E0D4] hover:border-[#C9B9A3]"}`}>
                  <span className="block font-display text-xl text-[#2D2A26]">{label}</span>
                  <span className="text-xs text-[#6E685F]">{id === "psychic-reading" ? (prices.options[0] ? `From ${money(prices.options[0].price)}` : "") : (prices.ritual ? `${money(prices.ritual)} USD` : "")}</span>
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              <input data-testid="booking-form-name-input" className={field} placeholder="Your name" value={form.name} onChange={set("name")} />
              <input data-testid="booking-form-email-input" type="email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
              {isRitual ? (
                <>
                  <select data-testid="booking-form-moon-select" className={`${field} sm:col-span-2`} value={form.moon_cycle} onChange={set("moon_cycle")}>
                    {!moons.length && <option value="">Next dates coming soon</option>}
                    {moons.map((m) => <option key={m.date + m.phase} value={moonLabel(m)}>{moonLabel(m)}</option>)}
                  </select>
                  <select data-testid="booking-form-ritual-select" className={`${field} sm:col-span-2`} value={form.ritual_type} onChange={set("ritual_type")}>
                    {RITUALS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <div data-testid="booking-form-duration" className="sm:col-span-2 grid grid-cols-4 gap-2 pt-1">
                    {prices.options.map((o) => (
                      <button key={o.minutes} type="button" data-testid={`booking-duration-${o.minutes}`} onClick={() => setForm((f) => ({ ...f, duration: o.minutes }))}
                        className={`rounded-2xl border px-2 py-2.5 text-center transition-colors ${form.duration === o.minutes ? "border-[#A8613C] bg-[#F4F0E8]" : "border-[#E6E0D4] hover:border-[#C9B9A3]"}`}>
                        <span className="block text-sm text-[#2D2A26]">{o.minutes} min</span>
                        <span className="block text-xs text-[#6E685F]">{money(o.price)}</span>
                      </button>
                    ))}
                  </div>
                  <div data-testid="booking-form-contact-method" className="sm:col-span-2 grid grid-cols-2 gap-3 pt-1">
                    {METHODS.map(([id, label]) => (
                      <button key={id} type="button" data-testid={`booking-method-${id}`} onClick={() => setForm((f) => ({ ...f, contact_method: id }))}
                        className={`rounded-full border px-4 py-2.5 text-sm transition-colors ${form.contact_method === id ? "border-[#A8613C] bg-[#F4F0E8] text-[#2D2A26]" : "border-[#E6E0D4] text-[#5E584F] hover:border-[#C9B9A3]"}`}>{label}</button>
                    ))}
                  </div>
                  {form.contact_method === "phone" && <input data-testid="booking-form-phone-input" type="tel" className={`${field} sm:col-span-2`} placeholder="Phone number" value={form.phone} onChange={set("phone")} />}
                  <input data-testid="booking-form-date-input" type="date" className={field} value={form.preferred_date} onChange={set("preferred_date")} />
                  <select data-testid="booking-form-time-select" className={field} value={form.preferred_time} onChange={set("preferred_time")}>
                    {TIMES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </>
              )}
            </div>
            <textarea data-testid="booking-form-notes-input" rows={isRitual ? 4 : 3} className={`${field} resize-none`} placeholder={isRitual ? "The desires you want to manifest" : "What would you like to explore? (optional)"} value={form.notes} onChange={set("notes")} />
            <div className="pt-2">
              <button data-testid="booking-request-btn" disabled={busy} onClick={submit} className="btn-pill w-full bg-[#2D2A26] text-[#FBFBF8] hover:bg-[#A8613C] py-3.5 text-sm justify-center">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send booking request
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
